import { FormControlLabel, Switch } from '@mui/material';
import _ from 'lodash';

import { Button } from '../../../shared/Button';
import { Coordinate } from '../../model/components/Coordinate';
import { PopTextV2 } from '../../model/components/PopTextv2';
import { PathContextProvider, usePathContext } from '../../PathContext';
import { usePortalPostEditor } from '../../Post';
import { ColorInput } from './ColorInput';
import { PositionGridInput } from './PositionGridInput';
import {
    hexToUnityColor,
    POP_TEXT_V2_ROW_SEPARATOR,
    TranslationTable,
    UnityColor,
} from '@lifeserver/shared';

import { useSelectedLanguage } from '../../../hooks/selectedLanugage';

import { assert } from '../../../utils/assert';

import styles from './PopTextInput.module.scss';
import inputStyles from './TranslationTableInput.module.scss';

import { createTextKey } from '../../translationUtils';

const BLACK: UnityColor = hexToUnityColor('#000000');
const WHITE: UnityColor = hexToUnityColor('#FFFFFF');

const ALLOWED_COLORS = [BLACK, WHITE];

const MAX_ROWS = 3;

const getOppositeColor = (color: UnityColor): UnityColor =>
    _.isEqual(color, BLACK) ? WHITE : BLACK;

export function PopTextV2Input({
    value,
    onChange,
}: {
    value: PopTextV2;
    onChange?: (value: PopTextV2) => void;
}) {
    const { selectedLanguage } = useSelectedLanguage();
    const {
        postId,
        localizations,
        setLocalizations,
        postLocalizations,
        setPostLocalizations,
        localizationRecord,
    } = usePortalPostEditor();
    const { path } = usePathContext();

    const startColor = value.backgroundColor ?? BLACK;

    // Compute the single localization key for the text field
    const currentPath = createTextKey([...path, 'text']);
    const portalLocalizationId = value.text.$key ?? `${postId}.${currentPath}`;

    // Pending edits take priority over the stored value
    const fullText =
        localizationRecord?.[portalLocalizationId]?.[selectedLanguage] ??
        value.text[selectedLanguage] ??
        '';

    // Virtual rows are derived by splitting the stored text on the row separator
    const rows: string[] = fullText
        ? fullText.split(POP_TEXT_V2_ROW_SEPARATOR)
        : [''];

    // All row mutations funnel through here: joins rows with the row separator and
    // updates both the TranslationTable on the payload and the pending localizations.
    const updateTextForRows = (newRows: string[]) => {
        console.log(newRows, POP_TEXT_V2_ROW_SEPARATOR);
        const joined = newRows.join(POP_TEXT_V2_ROW_SEPARATOR);
        const newTranslationTable: TranslationTable = {
            ...value.text,
            $type: 'TranslationTable' as const,
            $key: portalLocalizationId,
            [selectedLanguage]: joined,
        };

        const filtered = localizations.filter(
            (loc) =>
                !(
                    loc.id === portalLocalizationId &&
                    loc.language === selectedLanguage
                ),
        );
        const prevLoc = localizations.find(
            (loc) =>
                loc.id === portalLocalizationId &&
                loc.language === selectedLanguage,
        );

        setLocalizations([
            ...filtered,
            {
                id: portalLocalizationId,
                language: selectedLanguage,
                translation: joined,
                metadataId: prevLoc?.metadataId ?? null,
            },
        ]);

        const exists = postLocalizations.some(
            (pl) =>
                pl.portalLocalizationId === portalLocalizationId &&
                pl.path === currentPath,
        );
        if (!exists) {
            setPostLocalizations([
                ...postLocalizations,
                {
                    portalLocalizationId,
                    path: currentPath,
                    portalPostId: postId ?? '',
                },
            ]);
        }

        onChange?.({
            ...value,
            text: newTranslationTable,
        });
    };

    const onRowChange = (rowIndex: number, newRowText: string) => {
        const newRows = [
            ...rows.slice(0, rowIndex),
            newRowText,
            ...rows.slice(rowIndex + 1),
        ];
        updateTextForRows(newRows);
    };

    const onAddRow = () => {
        updateTextForRows([...rows, '']);
    };

    const onRemoveRow = (indexToRemove: number) => {
        updateTextForRows([
            ...rows.slice(0, indexToRemove),
            ...rows.slice(indexToRemove + 1),
        ]);
    };

    const onStartColorChanged = (color: UnityColor) => {
        onChange?.({
            ...value,
            backgroundColor: color,
            textColor: getOppositeColor(color),
        });
    };

    const onAnimatedChange = (animated: boolean) => {
        onChange?.({
            ...value,
            animated,
        });
    };

    const onPositionChanged = (position: Coordinate) => {
        assert(value, 'FormData is undefined, yet it was updated');

        onChange?.({
            ...value,
            position,
        });
    };

    return (
        <div className={styles.container}>
            <FormControlLabel
                checked={value.animated ?? false}
                control={<Switch />}
                label="Animated"
                onChange={(_, animated) => {
                    onAnimatedChange(animated);
                }}
            />
            <PositionGridInput
                value={value.position}
                onChange={onPositionChanged}
            />
            <fieldset>
                <legend>Start Background Color</legend>
                <ColorInput
                    allowedColors={ALLOWED_COLORS}
                    value={startColor}
                    onChange={onStartColorChanged}
                />
            </fieldset>

            <fieldset className={styles.rowContainer}>
                <PathContextProvider segment="text">
                    <legend>Rows* ({selectedLanguage})</legend>

                    {rows.map((row, i) => (
                        <div key={i} className={styles.rowTextContainer}>
                            <textarea
                                className={inputStyles.textarea}
                                value={row}
                                onChange={(e) => onRowChange(i, e.target.value)}
                            />
                            <Button
                                color="danger"
                                onClick={() => onRemoveRow(i)}
                                disabled={rows.length === 1}
                            >
                                X
                            </Button>
                        </div>
                    ))}

                    <Button
                        disabled={rows.length >= MAX_ROWS}
                        color="secondary"
                        onClick={onAddRow}
                    >
                        New Row
                    </Button>
                </PathContextProvider>
            </fieldset>
        </div>
    );
}
